import React from 'react'
import { T, Sheet, TIER } from './ui'

// Plain-English glossary — the one place a casual user can decode the ratings and the
// stats. Reached from the "?" in the header. Research language only; no bet advice.

const TIER_ORDER = ['ELITE', 'BEST BET', 'STRONG', 'PLAY', 'WATCH', 'PASS']
const TIER_MEAN = {
  ELITE: 'Our highest conviction — everything lines up.',
  'BEST BET': 'Top-rated — a strong, well-supported read.',
  STRONG: 'A solid lean with the model behind it.',
  PLAY: 'Moderate — leans our way, but lighter support.',
  WATCH: 'On the radar — not enough edge to lead with.',
  PASS: 'Low conviction — the model isn’t there on this one.',
}

const TERMS = [
  ['To hit / To score', 'The model’s chance the player clears the line (or scores a TD). Higher = more likely, per our model — not a guarantee.'],
  ['Edge', 'How much better the model’s chance is than the price the book is offering. Positive edge = the book looks a little cheap on that side.'],
  ['✓ Value', 'Flags a play the model rates as beatable at the posted price. It’s a research signal — never a suggested wager or stake size.'],
  ['Line', 'The number to clear (e.g. Over 68.5 yards). “Book line” is the sportsbook’s posted number; “Model line” is our own estimate before a book line is posted.'],
  ['Best price / line shopping', 'Which sportsbook is offering the best number for that play right now. Tap a play to see every book.'],
  ['Confidence / Rating', 'How much support the model has for a read. Every play carries one rating — Elite, Top Rated, Strong, Moderate, Watch or Low (see the list above). Game cards use a separate 0–100 game grade.'],
  ['CLV · “beat the close”', 'Closing-line value — did our number beat where the line finished. It’s the single strongest sign a play was priced right, tracked after the fact.'],
  ['Calibration / Brier', 'A check on honesty: when we say 65%, does it actually hit ~65%? Brier scores that overall — lower is better.'],
]

const ADVANCED = [
  ['EPA', 'Expected Points Added — how much each play helps a team score, on average.'],
  ['NGS', 'Next Gen Stats — the NFL’s player-tracking data (speed, separation, etc.).'],
  ['aDOT', 'Average Depth of Target — how far downfield a receiver is typically thrown to.'],
  ['CPOE', 'Completion % Over Expected — a QB’s accuracy vs. how hard his throws were.'],
  ['RYOE', 'Rushing Yards Over Expected — yards a back adds beyond what the blocking gave him.'],
  ['de-vig / no-vig', 'The book’s price with its built-in margin stripped out — the “fair” odds underneath.'],
]

// Walkthrough — the "how do I actually use this" guide a new user needs before the decoder.
const FIND = [
  ['This Week', 'The home board — the week’s plays, model-ranked. Flip the board between Top rated, Most likely (to cash) and Unders. Start here.'],
  ['Tap any game', 'Opens the game breakdown — how to attack each defense and the players who exploit it. “The Read” (a 60-second preview) is in its Matchup tab.'],
  ['Matchup Sheets', 'The softest defenses this week and exactly who’s facing them (rank 1 = allows the most).'],
  ['Play Lab 🧪', 'The generator: pick a stat (Touchdowns, Receptions, Yards…) and all games or just the ones you want, tap Generate, and get the model’s highest-probability plays ranked. Turn any list into a share card.'],
  ['Touchdowns', 'Everything about who scores, in one tab: Pay Dirt (best red-zone setups), Most likely, TD Matchups (defenses that leak TDs to a role) and Due.'],
  ['More & My Board', 'Every other tool lives under More. My Board lets you pin anything in the app — live cards or one-tap shortcuts — in your own order.'],
]
const ANALYZE = [
  ['1 · Start with the hit %', 'Our model’s chance the play clears the line (or scores). Higher = more likely — never a guarantee.'],
  ['2 · Check the edge', 'How much our number beats the book’s price. Positive edge = the book looks cheap on that side. That’s the value.'],
  ['3 · Read the rating', 'One rating on every play, Elite → Low. Elite, Top Rated and Strong are the model’s recommended reads (green); Moderate is a lean; Watch and Low are lean-outs.'],
  ['4 · Mind the matchup', 'Rank 1 = the softest defense to attack. A high hit % into a #1-soft spot is the strongest kind of read.'],
  ['5 · Shop the line', 'Tap a play to see every book’s number — the best price can swing a close call.'],
]

function Row({ term, def }) {
  return (
    <div style={{ padding: '10px 0', borderTop: `1px solid ${T.border}` }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>{term}</div>
      <div style={{ fontSize: 12.5, color: T.muted, lineHeight: 1.5, marginTop: 2 }}>{def}</div>
    </div>
  )
}

function StepCard({ items, accent }) {
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>
      {items.map(([h, d], i) => (
        <div key={h} style={{ padding: '11px 13px', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: accent || T.text }}>{h}</div>
          <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.5, marginTop: 2 }}>{d}</div>
        </div>
      ))}
    </div>
  )
}

export default function GlossarySheet({ onClose }) {
  return (
    <Sheet onClose={onClose}>
      <div style={{ padding: '10px 16px 28px' }}>
        <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 2 }}>How to read LINEBRKR</div>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 16, lineHeight: 1.5 }}>
          Where to find the reads, how to analyze them, and what every number means. LINEBRKR is a research tool — none of this is betting advice.
        </div>

        {/* Walkthrough — find the reads */}
        <div style={{ fontSize: 11, fontWeight: 800, color: T.green, letterSpacing: '0.08em', marginBottom: 8 }}>① WHERE TO FIND THE READS</div>
        <StepCard items={FIND} accent={T.green} />

        {/* Walkthrough — analyze a read */}
        <div style={{ fontSize: 11, fontWeight: 800, color: T.green, letterSpacing: '0.08em', marginBottom: 8 }}>② HOW TO ANALYZE A READ</div>
        <StepCard items={ANALYZE} />

        {/* Our honesty — the differentiator */}
        <div style={{ background: T.greenDim, border: `1px solid ${T.green}44`, borderRadius: 12, padding: '12px 14px', marginBottom: 22 }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: T.green, marginBottom: 3 }}>We grade in public</div>
          <div style={{ fontSize: 12, color: T.muted2, lineHeight: 1.5 }}>
            Every play settles against the real box score — wins <b>and</b> losses — and the record is right there in the Record tab. If a number says 65%, we track whether it actually hits ~65%. No cherry-picked screenshots.
          </div>
        </div>

        <div style={{ fontSize: 15, fontWeight: 900, marginBottom: 4 }}>What the numbers mean</div>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 14, lineHeight: 1.5 }}>
          A quick decoder for the ratings and stats.
        </div>

        {/* Tier legend */}
        <div style={{ fontSize: 11, fontWeight: 800, color: T.green, letterSpacing: '0.08em', marginBottom: 8 }}>CONVICTION TIERS</div>
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>
          {TIER_ORDER.map((t, i) => {
            const s = TIER[t] || {}
            return (
              <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 12px', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: s.fg, background: s.bg, border: `1px solid ${s.bd}`,
                  borderRadius: 20, padding: '3px 9px', letterSpacing: '0.05em', whiteSpace: 'nowrap', flexShrink: 0 }}>{s.icon} {s.label}</span>
                <span style={{ fontSize: 12, color: T.muted, lineHeight: 1.4 }}>{TIER_MEAN[t]}</span>
              </div>
            )
          })}
        </div>

        {/* Core terms */}
        <div style={{ fontSize: 11, fontWeight: 800, color: T.green, letterSpacing: '0.08em' }}>THE ESSENTIALS</div>
        {TERMS.map(([term, def]) => <Row key={term} term={term} def={def} />)}

        {/* Advanced */}
        <div style={{ fontSize: 11, fontWeight: 800, color: T.muted, letterSpacing: '0.08em', marginTop: 18 }}>ADVANCED (PRO)</div>
        {ADVANCED.map(([term, def]) => <Row key={term} term={term} def={def} />)}

        <div style={{ fontSize: 11, color: T.faint, marginTop: 18, lineHeight: 1.5, textAlign: 'center' }}>
          21+ · Research &amp; analytics only — not betting advice, not a sportsbook.
        </div>
      </div>
    </Sheet>
  )
}
